<template>
    <div
        :id="`call-activity-${element.id}`"
        :style="containerStyles"
        ref="callActivityOverlay"
    >
        <Popup
            v-if="!isEditing && selectedProcess"
            :anchor="callActivityOverlayElement!"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
        >
            <GenericCard
                :entityId="selectedProcess.identity"
                :entityType="EntityType.PROCESS"
            />
        </Popup>
        <div
            v-if="isEditing"
            class="process-selector"
            @dblclick="handleSelectorDoubleClick"
        >
            <Select
                v-model="selectedProcess"
                ref="process-selector-element"
                :options="products"
                optionGroupLabel="name"
                optionGroupChildren="processes"
                optionLabel="name"
                placeholder="..."
                @change="changeProcess"
                :disabled="disableSelector"
                @hide="disableSelector = true"
            >
                <template #value="slotProps">
                    <div v-if="slotProps.value" class="flex items-center">
                        <div>{{ slotProps.value.name }}</div>
                    </div>
                    <span v-else>
                        {{ slotProps.placeholder }}
                    </span>
                </template>
                <template #optiongroup="slotProps">
                    <div class="select-option-group">
                        <i class="pi pi-box"></i>
                        <span>{{ slotProps.option.name }}</span>
                    </div>
                </template>
                <template #option="slotProps">
                    <div class="select-option">
                        <div>{{ slotProps.option.name }}</div>
                    </div>
                </template>
            </Select>
        </div>
        <RouterLink
            v-else-if="selectedProcess"
            :id="`process-link-${selectedProcess.identity}`"
            class="process-link"
            :class="{
                hover: isHovering,
            }"
            @mousedown="followCallActivity"
            :to="{
                name: 'processPage',
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    processId: selectedProcess.identity,
                },
            }"
            :aria-label="selectedProcess?.name"
        />
    </div>
</template>

<script setup lang="ts">
import Popup from "@/components/Popup.vue";
import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import { computed, ComputedRef, ref, toRef, useTemplateRef, watch } from "vue";
import { type Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { useBpmnManager } from "@/composables/bpmn-manager";
import {
    EntityType,
    ProductInTree,
    useCurrentEntityManager,
} from "@/stores/entity-store";
import { useEditManager } from "@/composables/edit-manager";
import { useRoute } from "vue-router";
import { Process } from "@/stores/processes-store";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import Select, { SelectChangeEvent } from "primevue/select";
import { HIGH_PRIORITY } from "@/composables/bpmn-manager/helpers";

const { isEditing } = useEditManager();
const route = useRoute();
const { domain: currentDomain, process: currentProcess } =
    useCurrentEntityManager();
const callActivityOverlayElement = useTemplateRef("callActivityOverlay");

const isHovering = ref(false);
const processSelectorElement = useTemplateRef<typeof Select>(
    "process-selector-element"
);
const disableSelector = ref(true);

const props = defineProps<{
    element: BPMNElement;
    bpmnManager: ReturnType<typeof useBpmnManager>;
}>();

const element = toRef(props.element);
const selectedProcess = ref<Process>();

const containerStyles = computed(() => {
    // Add padding to the element container to overlay BPMN element border
    return {
        "width": element.value.width + 16 + "px",
        "height": element.value.height + 16 + "px",
    };
});

const products: ComputedRef<ProductInTree[]> = computed(() => {
    if (!currentDomain.value?.products) return [];

    return currentDomain.value.products.map((product) => {
        // Filter out current process.
        return {
            ...product,
            processes: product.processes.filter((process) => {
                return process.identity !== currentProcess.value?.identity;
            }),
        };
    });
});

const elementProcessId = computed(() => {
    return getBusinessObject(element.value).processId;
});

const handleMouseEnter = () => {
    isHovering.value = true;
    props.bpmnManager.eventBus?.fire("element.hover", {
        element: element.value,
        hover: true,
    });
};

const handleMouseLeave = () => {
    isHovering.value = false;
    props.bpmnManager.eventBus?.fire("element.out", {
        element: element.value,
        hover: false,
    });
};

const handleSelectorDoubleClick = () => {
    if (!isEditing.value) return;
    disableSelector.value = false;
    processSelectorElement.value?.show();
};

const followCallActivity = () => {
    // Hide the popover before navigating
    isHovering.value = false;
};

watch(
    () => props.bpmnManager.eventBus,
    (eventBus) => {
        if (eventBus) {
            eventBus!.on(
                "element.dblclick",
                HIGH_PRIORITY,
                ({
                    element: bpmnElement,
                }: Event & { element: BPMNElement }) => {
                    if (element.value.id == bpmnElement.id) {
                        processSelectorElement.value?.show();
                        return false;
                    }
                }
            );
        }
    },
    { immediate: true }
);

watch(
    [
        elementProcessId,
        () => currentDomain.value?.products.flatMap((p) => p.processes),
    ],
    ([processId, processes]) => {
        if (!processes?.length) return;

        const process = processes?.find(
            (process) => process.identity === processId
        );
        if (process) {
            selectedProcess.value = process;
        }
    },
    { immediate: true }
);

const changeProcess = (event: SelectChangeEvent) => {
    selectedProcess.value = event.value;

    if (selectedProcess.value && element.value) {
        props.bpmnManager.updateElementProperties(element.value.id, {
            name: selectedProcess.value.name,
            processId: selectedProcess.value.identity,
        });
        // Make sure the change is picked up.
        props.bpmnManager.eventBus?.fire("commandStack.changed");
    }
};
</script>

<style scoped lang="scss">
.process-selector,
.process-link {
    padding: 8px;
    text-align: center;
}

.process-selector {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    font-size: 12px;
    pointer-events: none;

    :deep(.p-select) {
        width: 100%;
        height: 100%;
        display: flex;
        background: none;
        border: none;
        box-shadow: none;

        .p-select-dropdown {
            width: 0;
        }
        .p-select-label {
            display: none;
        }

        &.p-invalid {
            .p-select-label,
            .p-select-dropdown {
                color: var(--p-select-invalid-border-color);
            }
        }
    }
}
.process-link {
    text-decoration: none;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: var(--stekz-biz-color);
    font-size: 0.7em;
    background: "transparent";

    &.hover {
        color: white;
    }

    * {
        max-width: 80%;
        text-wrap-mode: wrap;
        overflow-wrap: break-word;

        // Prevent child elements from stealing the hover event
        pointer-events: none;
    }
}
.select-option-group {
    display: flex;
    gap: 0.25em;
    color: var(--stekz-biz-color);
    align-items: center;
}

.select-option {
    padding-left: 1.3em;
}
</style>
